import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { matchesAPI } from '../services/api';
import toast from 'react-hot-toast';

function StatsPage() {
  const { matchId } = useParams();
  const [match, setMatch] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState('all');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  const fetchData = async () => {
    try {
      const [matchData, statsData] = await Promise.all([
        matchesAPI.getMatch(matchId),
        matchesAPI.getStats(matchId)
      ]);

      setMatch(matchData);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast.error('Failed to load match statistics');
    } finally {
      setLoading(false);
    }
  };

  const exportStats = async () => {
    try {
      const dataStr = JSON.stringify(stats, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);

      const exportFileDefaultName = `volleyball-stats-${matchId}.json`;

      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();

      toast.success('Stats exported successfully');
    } catch (error) {
      toast.error('Failed to export stats');
    }
  };

  if (loading) {
    return <div className="loading">Loading statistics...</div>;
  }

  if (!match || !stats) {
    return (
      <div className="loading">
        <p>Match or statistics not found</p>
        <Link to="/matches" className="btn btn-primary">Back to Matches</Link>
      </div>
    );
  }

  const playerStats = stats.events_by_player || [];
  const filteredStats = selectedTeam === 'all'
    ? playerStats
    : playerStats.filter(p => p.team === selectedTeam);

  const teamTotals = {
    attacks: filteredStats.reduce((sum, p) => sum + (p.attacks || 0), 0),
    kills: filteredStats.reduce((sum, p) => sum + (p.kills || 0), 0),
    errors: filteredStats.reduce((sum, p) => sum + (p.attack_errors || 0), 0),
    serves: filteredStats.reduce((sum, p) => sum + (p.serves || 0), 0),
    aces: filteredStats.reduce((sum, p) => sum + (p.aces || 0), 0),
    digs: filteredStats.reduce((sum, p) => sum + (p.digs || 0), 0),
    blocks: filteredStats.reduce((sum, p) => sum + (p.blocks || 0), 0),
  };

  return (
    <div className="stats-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <h1>{match.title}</h1>
          <p style={{ color: '#64748b' }}>Match Statistics & Analytics</p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Link to={`/matches/${matchId}/review`} className="btn btn-secondary">
            Back to Review
          </Link>
          <button onClick={exportStats} className="btn btn-primary">
            📊 Export Data
          </button>
        </div>
      </div>

      {/* Team Filter */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <button
            className={`btn ${selectedTeam === 'all' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setSelectedTeam('all')}
          >
            All Players
          </button>
          <button
            className={`btn ${selectedTeam === 'home' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setSelectedTeam('home')}
          >
            {match.home_team || 'Home Team'}
          </button>
          <button
            className={`btn ${selectedTeam === 'away' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setSelectedTeam('away')}
          >
            {match.away_team || 'Away Team'}
          </button>
        </div>
      </div>

      {/* Team Summary */}
      <div className="grid grid-3" style={{ marginBottom: '2rem' }}>
        <div className="stat-card">
          <div className="stat-value">{teamTotals.kills}</div>
          <div className="stat-label">Total Kills</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{teamTotals.aces}</div>
          <div className="stat-label">Aces</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{teamTotals.digs}</div>
          <div className="stat-label">Digs</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{teamTotals.blocks}</div>
          <div className="stat-label">Blocks</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {teamTotals.attacks > 0 ? ((teamTotals.kills - teamTotals.errors) / teamTotals.attacks * 100).toFixed(1) : 0}%
          </div>
          <div className="stat-label">Attack %</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{teamTotals.attacks}</div>
          <div className="stat-label">Total Attacks</div>
        </div>
      </div>

      {/* Player Statistics Table */}
      <div className="card">
        <h2 style={{ marginBottom: '1rem' }}>Individual Player Stats</h2>
        
        {filteredStats.length === 0 ? (
          <p style={{ color: '#64748b', textAlign: 'center', padding: '2rem' }}>
            No statistics available. Please verify events in the Review page.
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Position</th>
                  <th>Attacks</th>
                  <th>Kills</th>
                  <th>Errors</th>
                  <th>Hit %</th>
                  <th>Serves</th>
                  <th>Aces</th>
                  <th>Digs</th>
                  <th>Blocks</th>
                  <th>Assists</th>
                </tr>
              </thead>
              <tbody>
                {filteredStats
                  .sort((a, b) => b.kills - a.kills)
                  .map(player => (
                    <tr key={player.player_id}>
                      <td><strong>#{player.jersey_number}</strong></td>
                      <td>{player.name || 'Unknown'}</td>
                      <td>{player.position || '-'}</td>
                      <td>{player.attacks}</td>
                      <td style={{ fontWeight: 'bold', color: '#16a34a' }}>{player.kills}</td>
                      <td style={{ color: '#dc2626' }}>{player.attack_errors}</td>
                      <td>
                        <span style={{ 
                          fontWeight: 'bold',
                          color: player.attack_percentage >= 30 ? '#16a34a' : 
                                 player.attack_percentage >= 20 ? '#2563eb' : '#64748b'
                        }}>
                          {player.attack_percentage}%
                        </span>
                      </td>
                      <td>{player.serves}</td>
                      <td style={{ fontWeight: 'bold', color: '#2563eb' }}>{player.aces}</td>
                      <td>{player.digs}</td>
                      <td>{player.blocks}</td>
                      <td>{player.assists}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Attack Leaders */}
      <div className="grid grid-2" style={{ marginTop: '2rem' }}>
        <div className="card">
          <h3 style={{ marginBottom: '1rem' }}>🔥 Top Attackers</h3>
          {filteredStats
            .sort((a, b) => b.kills - a.kills)
            .slice(0, 5)
            .map((player, index) => (
              <div key={player.player_id} style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                padding: '0.75rem',
                background: index === 0 ? '#fef3c7' : '#f9fafb',
                borderRadius: '4px',
                marginBottom: '0.5rem'
              }}>
                <div>
                  <strong>#{player.jersey_number}</strong> {player.name || 'Unknown'}
                  <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                    {player.attacks} attempts, {player.attack_percentage}%
                  </div>
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#16a34a' }}>
                  {player.kills}
                </div>
              </div>
            ))}
        </div>

        <div className="card">
          <h3 style={{ marginBottom: '1rem' }}>🛡️ Top Defenders</h3>
          {filteredStats
            .sort((a, b) => b.digs - a.digs)
            .slice(0, 5)
            .map((player, index) => (
              <div key={player.player_id} style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                padding: '0.75rem',
                background: index === 0 ? '#dbeafe' : '#f9fafb',
                borderRadius: '4px',
                marginBottom: '0.5rem'
              }}>
                <div>
                  <strong>#{player.jersey_number}</strong> {player.name || 'Unknown'}
                  <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                    {player.blocks} blocks
                  </div>
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2563eb' }}>
                  {player.digs}
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Service Leaders */}
      <div className="card" style={{ marginTop: '2rem' }}>
        <h3 style={{ marginBottom: '1rem' }}>⚡ Service Aces</h3>
        <div className="grid grid-3">
          {filteredStats
            .filter(p => p.aces > 0)
            .sort((a, b) => b.aces - a.aces)
            .map(player => (
              <div key={player.player_id} style={{ 
                padding: '1rem',
                background: '#f9fafb',
                borderRadius: '4px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#2563eb' }}>
                  {player.aces}
                </div>
                <div style={{ fontWeight: 'bold' }}>
                  #{player.jersey_number} {player.name || 'Unknown'}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                  {player.serves} serves, {player.service_errors} errors
                </div>
              </div>
            ))}
          {filteredStats.filter(p => p.aces > 0).length === 0 && (
            <p style={{ color: '#64748b' }}>No aces recorded</p>
          )}
        </div>
      </div>

      {/* Notes */}
      <div className="card" style={{ marginTop: '2rem', background: '#eff6ff' }}>
        <h4 style={{ marginBottom: '0.5rem' }}>📝 Notes</h4>
        <p style={{ fontSize: '0.875rem' }}>
          Statistics are calculated from verified events only. Return to the Review page to verify more events
          and improve accuracy. Attack percentage is calculated as (Kills - Errors) / Total Attacks × 100.
        </p>
      </div>
    </div>
  );
}

export default StatsPage;
